/**
 * Web / PWA push notifications via Firebase Cloud Messaging (web SDK).
 *
 * This is a NEW channel that runs ONLY on non-native web. It is fully gated:
 * if the required Firebase web config env vars are absent, initWebPush() does
 * nothing (no service worker registration, no errors) so the web build/runtime
 * is unaffected until the owner provisions a Firebase Web app + VAPID key.
 *
 * Required env (CRA inlines REACT_APP_* at build time):
 *   - REACT_APP_FIREBASE_API_KEY    (Firebase Console → Web app config)
 *   - REACT_APP_FIREBASE_APP_ID     (Firebase Console → Web app config)
 *   - REACT_APP_FIREBASE_VAPID_KEY  (Cloud Messaging → Web Push certificates)
 * If API_KEY or VAPID_KEY is missing, web push stays OFF.
 */
import { Capacitor } from '@capacitor/core';
import { authAPI } from '../services/api';

// Derivable defaults for the protrenerapp-4cb94 Firebase project. Only the
// secret/per-app bits (apiKey, appId, vapidKey) come from env.
const FIREBASE_DEFAULTS = {
  projectId: 'protrenerapp-4cb94',
  messagingSenderId: '125792528100',
  authDomain: 'protrenerapp-4cb94.firebaseapp.com',
  storageBucket: 'protrenerapp-4cb94.firebasestorage.app',
};

let initialized = false;
let swRegistration = null;

function isWebPushConfigured() {
  return Boolean(
    process.env.REACT_APP_FIREBASE_API_KEY &&
    process.env.REACT_APP_FIREBASE_VAPID_KEY
  );
}

/**
 * Initialize web push. Safe to call unconditionally — it returns early (OFF)
 * when running natively, when the browser lacks the needed APIs, or when the
 * Firebase web config env vars are missing. Never throws to the caller.
 *
 * @param {Object} opts
 * @param {Function} [opts.onMessage] - Called with the FCM payload for foreground messages.
 * @param {string}   [opts.language]  - Optional language code (e.g. 'hr') to register with the token.
 */
export async function initWebPush({ onMessage, language } = {}) {
  try {
    // --- Hard gates: stay OFF unless this is configured non-native web ---
    if (Capacitor.isNativePlatform()) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (!isWebPushConfigured()) return;

    if (initialized) return;
    initialized = true;

    // --- Permission: only prompt when promptable; bail on denied ---
    if (Notification.permission === 'denied') {
      initialized = false;
      return;
    }
    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      if (result !== 'granted') {
        initialized = false;
        return;
      }
    }
    if (Notification.permission !== 'granted') {
      initialized = false;
      return;
    }

    const firebaseConfig = {
      ...FIREBASE_DEFAULTS,
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
    };

    // Register the SW WITH the config in the query so the SW can read it
    // (single source of truth = the app's env, no duplicated hardcoded config).
    const swUrl =
      '/firebase-messaging-sw.js?firebaseConfig=' +
      encodeURIComponent(JSON.stringify(firebaseConfig));
    const registration = await navigator.serviceWorker.register(swUrl);
    swRegistration = registration;

    // Lazy-load the modular web SDK so it never enters the bundle's critical
    // path and never runs when web push is off.
    const { initializeApp } = await import('firebase/app');
    const { getMessaging, getToken, onMessage: onFcmMessage } = await import('firebase/messaging');

    const app = initializeApp(firebaseConfig);
    const messaging = getMessaging(app);

    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      console.warn('[WEBPUSH] No FCM token returned; web push not registered');
      return;
    }

    const lang = language || 'hr';
    try {
      await authAPI.registerDeviceToken({ token, platform: 'web', language: lang });
      console.log('[WEBPUSH] Token registered with backend OK');
    } catch (err) {
      console.error('[WEBPUSH] Token registration failed:', err?.response?.status, err?.response?.data || err.message);
    }

    // Foreground messages (page focused). Background ones are handled by the SW.
    onFcmMessage(messaging, (payload) => {
      if (onMessage) onMessage(payload);
    });
  } catch (err) {
    // Never throw to the caller — web push is best-effort.
    console.warn('[WEBPUSH] init failed (web push disabled for this session):', err?.message || err);
    initialized = false;
  }
}

/**
 * Tear down web push. Best-effort; never throws.
 */
export async function cleanupWebPush() {
  try {
    initialized = false;
    if (swRegistration) {
      // Keep the SW registered (it caches the FCM config) but drop our ref.
      swRegistration = null;
    }
  } catch (err) {
    console.warn('[WEBPUSH] cleanup failed:', err?.message || err);
  }
}
